import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { AlertTriangle, HelpCircle, Trash2 } from 'lucide-react';
import './ConfirmProvider.css';

/**
 * Диалоги подтверждения и ввода вместо системных окон браузера.
 *
 * По системе было 135 вызовов confirm/alert/prompt: серое окно ОС, в которое нельзя
 * положить объяснение последствий, нельзя назвать кнопку по-человечески («Удалить
 * короб» вместо «ОК») и которое на терминале выглядит чужеродно. Здесь тот же вызов,
 * но свой: `if (!await confirm({...})) return;` — заменяется механически.
 */

const ConfirmContext = createContext(null);

/**
 * По тексту вопроса понимаем, что за действие, и подписываем кнопку глаголом.
 * «Подтвердить» человек читает как «ОК» и жмёт не глядя — глагол заставляет
 * прочитать, особенно когда действие необратимое.
 */
const ACTION_WORDS = [
  [/^удал|удалить/i, 'Удаление', 'Удалить', true],
  [/^отменить|^отмен/i, 'Отмена', 'Отменить', true],
  [/^сбросить|^очистить/i, 'Сброс', 'Сбросить', true],
  [/^вернуть|^откатить/i, 'Возврат', 'Вернуть', false],
  [/^отгрузить|^передать|^отправить/i, 'Отгрузка', 'Отгрузить', false],
  [/^завершить|^закрыть/i, 'Завершение', 'Завершить', false],
  [/^восстановить/i, 'Восстановление', 'Восстановить', false],
  [/^пересчитать|^перерасч/i, 'Пересчёт', 'Пересчитать', false],
  [/^включить|^выключить|^применить|^проставить/i, 'Подтвердите действие', 'Применить', false],
  [/^создать|^добавить|^сформировать/i, 'Подтвердите действие', 'Создать', false],
  [/^печат|^напечатать/i, 'Печать', 'Печатать', false],
];

function guessAction(message) {
  const text = String(message || '').trim();
  for (const [re, title, confirmText, danger] of ACTION_WORDS) {
    if (re.test(text)) return { title, confirmText, danger };
  }
  return {};
}

export function ConfirmProvider({ children }) {
  const [dialog, setDialog] = useState(null);
  const resolverRef = useRef(null);

  const close = useCallback((result) => {
    setDialog(null);
    if (resolverRef.current) {
      resolverRef.current(result);
      resolverRef.current = null;
    }
  }, []);

  /** Спросить подтверждение. Возвращает true/false. */
  const confirm = useCallback((opts) => {
    const cfg = typeof opts === 'string' ? { message: opts } : (opts || {});
    setDialog({ mode: 'confirm', ...guessAction(cfg.message), ...cfg });
    return new Promise(resolve => { resolverRef.current = resolve; });
  }, []);

  /** Спросить значение. Возвращает строку или null, если отказались. */
  const prompt = useCallback((opts) => {
    const cfg = typeof opts === 'string' ? { message: opts } : (opts || {});
    setDialog({ mode: 'prompt', value: cfg.defaultValue || '', ...cfg });
    return new Promise(resolve => { resolverRef.current = resolve; });
  }, []);

  /** Показать сообщение, которое нельзя пропустить (замена alert). */
  const notify = useCallback((opts) => {
    const cfg = typeof opts === 'string' ? { message: opts } : (opts || {});
    setDialog({ mode: 'notify', ...cfg });
    return new Promise(resolve => { resolverRef.current = resolve; });
  }, []);

  const onKeyDown = (e) => {
    if (e.key === 'Escape') close(dialog?.mode === 'prompt' ? null : false);
    if (e.key === 'Enter' && dialog?.mode !== 'prompt') close(dialog?.mode === 'notify' ? undefined : true);
  };

  const danger = dialog?.danger !== false && dialog?.variant !== 'info';
  const Icon = dialog?.mode === 'notify' ? HelpCircle : (dialog?.danger ? Trash2 : AlertTriangle);

  return (
    <ConfirmContext.Provider value={{ confirm, prompt, notify }}>
      {children}
      {dialog && (
        <div className="cfm-overlay" onClick={() => close(dialog.mode === 'prompt' ? null : false)}
             onKeyDown={onKeyDown} tabIndex={-1} role="dialog" aria-modal="true">
          <div className="cfm-box" onClick={e => e.stopPropagation()}>
            <div className="cfm-head">
              <div className={`cfm-icon ${danger ? 'cfm-icon--danger' : ''}`}><Icon size={20} /></div>
              <div className="cfm-title">{dialog.title || (dialog.mode === 'notify' ? 'Внимание' : 'Подтвердите действие')}</div>
            </div>

            {dialog.message && <div className="cfm-message">{dialog.message}</div>}
            {/* Последствия пишем отдельной строкой: человек должен понимать, что произойдёт */}
            {dialog.detail && <div className="cfm-detail">{dialog.detail}</div>}

            {dialog.mode === 'prompt' && (
              <input
                className="cfm-input"
                autoFocus
                defaultValue={dialog.defaultValue || ''}
                placeholder={dialog.placeholder || ''}
                onKeyDown={e => { if (e.key === 'Enter') close(e.currentTarget.value); }}
                ref={el => { if (el) el.focus(); }}
                id="cfm-input"
              />
            )}

            <div className="cfm-actions">
              {dialog.mode !== 'notify' && (
                <button className="cfm-btn cfm-btn--ghost"
                        onClick={() => close(dialog.mode === 'prompt' ? null : false)}>
                  {dialog.cancelText || 'Отмена'}
                </button>
              )}
              <button
                className={`cfm-btn ${danger ? 'cfm-btn--danger' : 'cfm-btn--primary'}`}
                autoFocus={dialog.mode !== 'prompt'}
                onClick={() => {
                  if (dialog.mode === 'prompt') {
                    const el = document.getElementById('cfm-input');
                    close(el ? el.value : '');
                  } else {
                    close(dialog.mode === 'notify' ? undefined : true);
                  }
                }}
              >
                {dialog.confirmText || (dialog.mode === 'notify' ? 'Понятно' : 'Подтвердить')}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  // Провайдер мог не подключиться (например, на экране логина) — тогда ведём себя
  // как раньше, чтобы действие не сломалось молча
  if (!ctx) {
    return {
      confirm: async (o) => window.confirm(typeof o === 'string' ? o : o?.message || ''),
      prompt: async (o) => window.prompt(typeof o === 'string' ? o : o?.message || ''),
      notify: async (o) => window.alert(typeof o === 'string' ? o : o?.message || ''),
    };
  }
  return ctx;
}
